import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { traduci, linguaDelUtente } from './i18n';
import { Toaster } from './components/ui/sonner';
import { AuthProvider } from './context/AuthContext';
import { ProtectedLayout } from './components/layouts/ProtectedLayout';
import { NotificationBanner } from './components/features/NotificationBanner';
import Login from './pages/Login';
import Register from './pages/Register';
import RegisterCollaboratore from './pages/RegisterCollaboratore';
import HomeCliente from './pages/HomeCliente';
import ImmobiliCliente from './pages/ImmobiliCliente';
import DocumentiCliente from './pages/DocumentiCliente';
import FirmaDocumento from './pages/FirmaDocumento';
import SchedaImmobile from './pages/staff/SchedaImmobile';
import Mutui from './pages/staff/Mutui';
import Social from './pages/staff/Social';
import DaProporre from './pages/staff/DaProporre';
import SchedaImmobileCliente from './pages/SchedaImmobileCliente';
import { useAuth } from './context/AuthContext';
import ModificaAgenzia from './pages/ModificaAgenzia';
import CalendarioCliente from './pages/CalendarioCliente';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TerminiServizio from './pages/TerminiServizio';
import Installa from './pages/Installa';
import Profilo from './pages/ProfiloStaff';
import ProfiloCliente from './pages/ProfiloCliente';
import Condivisi from './pages/Condivisi';
import ArchivioDocumenti from './pages/ArchivioDocumenti';
import RinnovoPermesso from './pages/RinnovoPermesso';
import Impostazioni from './pages/Impostazioni';
import DoppioniAgenzie from './pages/DoppioniAgenzie';
import Oggi from './pages/staff/Oggi';
import Agenda from './pages/staff/Agenda';
import Clienti from './pages/staff/Clienti';
import Numeri from './pages/staff/Numeri';
import Documenti from './pages/staff/Documenti';
import SchedaCliente from './pages/staff/SchedaCliente';
import Contatti from './pages/staff/Contatti';
import SchedaContatto from './pages/staff/SchedaContatto';
import Agenzie from './pages/staff/Agenzie';
import SchedaAgenzia from './pages/staff/SchedaAgenzia';
import Collaboratori from './pages/staff/Collaboratori';
import CruscottoMese from './pages/staff/CruscottoMese';
import Provvigioni from './pages/staff/Provvigioni';
import NonTrovata from './pages/NonTrovata';
import ErroreInatteso from './components/ErroreInatteso';
import './App.css';

// Scheda immobile: il cliente ha la sua, admin e collaboratori quella dello staff (stessa struttura, più le azioni)
// /collaboratore: Oggi del collaboratore (il cliente che ci finisce per sbaglio va alla sua home)
const OggiCollaboratore = () => {
  const { userType } = useAuth();
  if (userType === 'admin') return <Navigate to="/admin" replace />;
  if (userType !== 'collaboratore') return <Navigate to="/dashboard" replace />;
  return <Oggi />;
};

const SchedaImmobilePerRuolo = () => {
  const { userType } = useAuth();
  return userType === 'cliente' ? <SchedaImmobileCliente /> : <SchedaImmobile />;
};

// Il profilo: il cliente ha il suo nel nuovo stile (schede), admin e collaboratori la loro pagina
const ProfiloPerRuolo = () => {
  const { userType } = useAuth();
  if (userType === 'cliente') return <ProtectedLayout nuovoStile><ProfiloCliente /></ProtectedLayout>;
  return <ProtectedLayout nuovoStile><Profilo scheda={null} /></ProtectedLayout>;
};

// Il calendario: il cliente ha il suo (/calendario), lo staff l'agenda
const CalendarioPerRuolo = () => {
  const { userType } = useAuth();
  if (userType === 'admin' || userType === 'collaboratore') return <Navigate to="/admin/agenda" replace />;
  return <ProtectedLayout nuovoStile><CalendarioCliente /></ProtectedLayout>;
};

// I documenti: il cliente ha i suoi (/documenti), lo staff la pagina Documenti (prima lo staff su /documenti leggeva
// "Cliente non trovato": la pagina usava il suo id come id del cliente)
const DocumentiPerRuolo = () => {
  const { userType } = useAuth();
  if (userType === 'admin' || userType === 'collaboratore') return <Navigate to="/admin/documenti" replace />;
  return <ProtectedLayout nuovoStile><DocumentiCliente /></ProtectedLayout>;
};

// Una pagina che si rompe mostra un messaggio invece di restare bianca; cambiando pagina si riparte
const RotteSicure = () => {
  const { pathname } = useLocation();
  return <ErroreInatteso key={pathname}><Rotte /></ErroreInatteso>;
};

// /collaboratore/clienti (il vecchio cruscotto del collaboratore): la lista dei clienti; con ?cliente=<id> la sua scheda
const VecchiClientiCollaboratore = () => {
  const cliente = new URLSearchParams(useLocation().search).get('cliente');
  return <Navigate to={cliente ? `/admin/clienti/${encodeURIComponent(cliente)}` : '/admin/clienti'} replace />;
};

// /il-mio-calendario?google_connected=true (o ?google_error=…): il ritorno dal collegamento a Google Calendar del
// cliente (backend, google_calendar_client_callback). Al calendario con il parametro: prima si perdeva e il calendario
// non diceva né "collegato" né l'errore
const RitornoDaGoogle = () => {
  const { search } = useLocation();
  return <Navigate to={{ pathname: '/calendario', search }} replace />;
};

// Indirizzi di prima (link nelle email, segnalibri): portano alle pagine nuove
const Vai = ({ a }) => {
  const params = useParams();
  return <Navigate to={a.replace(/:(\w+)/g, (_, k) => params[k])} replace />;
};

// Come `Vai`, ma si porta dietro l'ancora e la parte dopo il "?": serve agli indirizzi che girano fuori di qui e
// che puntano a un pezzo di pagina. Il popup dell'estensione apriva `/impostazioni#estrattore-attivita` (una pagina
// che non esiste: le impostazioni stanno sotto /admin), e senza l'ancora si arriverebbe in cima alle impostazioni
// invece che sul diario dell'estrattore.
const VaiConAncora = ({ a }) => {
  const { hash, search } = useLocation();
  return <Navigate to={`${a}${search}${hash}`} replace />;
};

// Tutte le rotte (a parte, così i test di navigazione le provano con un utente finto: src/Navigazione.test.jsx)
export const Rotte = () => (
  <Routes>
    {/* Route pubbliche */}
    <Route path="/login" element={<Login />} />
    <Route path="/register" element={<Register />} />
    {/* Le email di invito spedite fino al 30/09/2026 portavano a «/registrati», che non è mai esistito: chi
        cliccava trovava la pagina «non trovata». Il link adesso è /register (backend/notifiche.py), ma quelle
        email sono già nelle caselle delle persone: questo rinvio le fa funzionare comunque. Non si toglie. */}
    <Route path="/registrati" element={<Navigate to="/register" replace />} />
    <Route path="/register-collaboratore" element={<RegisterCollaboratore />} />
    <Route path="/privacy" element={<PrivacyPolicy />} />
    <Route path="/termini" element={<TerminiServizio />} />
    <Route path="/condivisi/:token" element={<Condivisi />} />
    {/* Il link che si manda su WhatsApp e nelle email: la guida per mettere l'app sulla schermata Home. Deve
        aprirsi senza essere collegati, perché spesso è la prima cosa che il cliente apre (pages/Installa.jsx) */}
    <Route path="/installa" element={<Installa />} />

    {/* Redirect root */}
    <Route path="/" element={<Navigate to="/dashboard" replace />} />

    {/* Route protette - Solo Admin */}
    <Route path="/admin" element={<ProtectedLayout adminOnly nuovoStile><Oggi /></ProtectedLayout>} />
    <Route path="/admin/agenda" element={<ProtectedLayout adminOnly allowCollaboratore nuovoStile><Agenda /></ProtectedLayout>} />
    <Route path="/admin/clienti" element={<ProtectedLayout adminOnly allowCollaboratore nuovoStile><Clienti /></ProtectedLayout>} />
    <Route path="/admin/clienti/:id" element={<ProtectedLayout adminOnly allowCollaboratore nuovoStile><SchedaCliente /></ProtectedLayout>} />
    <Route path="/admin/clienti/:id/completa" element={<Vai a="/admin/clienti/:id" />} />
    {/* I contatti (chi non è ancora cliente): li vedono admin e collaboratori, ognuno i propri (backend/contatti.py) */}
    <Route path="/admin/contatti" element={<ProtectedLayout adminOnly allowCollaboratore nuovoStile><Contatti /></ProtectedLayout>} />
    <Route path="/admin/contatti/:id" element={<ProtectedLayout adminOnly allowCollaboratore nuovoStile><SchedaContatto /></ProtectedLayout>} />
    <Route path="/admin/collaboratori" element={<ProtectedLayout adminOnly nuovoStile><Collaboratori /></ProtectedLayout>} />
    <Route path="/admin/collaboratori/vecchia" element={<Navigate to="/admin/collaboratori" replace />} />
    {/* Il cruscotto del mese di una persona: l'admin da /admin/collaboratori, il responsabile per il suo gruppo */}
    <Route path="/admin/collaboratori/:id" element={<ProtectedLayout adminOnly allowCollaboratore nuovoStile><CruscottoMese /></ProtectedLayout>} />
    <Route path="/admin/estrattore" element={<Navigate to="/admin/impostazioni#estrattore" replace />} />
    <Route path="/admin/numeri" element={<ProtectedLayout adminOnly nuovoStile><Numeri /></ProtectedLayout>} />
    {/* I riepiloghi delle provvigioni: l'admin vede tutto, il collaboratore solo la sua quota (lo decide il server) */}
    <Route path="/admin/provvigioni" element={<ProtectedLayout adminOnly allowCollaboratore nuovoStile><Provvigioni /></ProtectedLayout>} />
    <Route path="/admin/agenda-vecchia" element={<Navigate to="/admin/agenda" replace />} />
    <Route path="/calendario" element={<CalendarioPerRuolo />} />
    <Route path="/il-mio-calendario" element={<RitornoDaGoogle />} />
    <Route path="/admin/agenzie" element={<ProtectedLayout adminOnly nuovoStile><Agenzie /></ProtectedLayout>} />
    <Route path="/admin/da-proporre" element={<ProtectedLayout adminOnly allowCollaboratore nuovoStile><DaProporre /></ProtectedLayout>} />
    <Route path="/admin/da-proporre/:clienteId" element={<ProtectedLayout adminOnly allowCollaboratore nuovoStile><DaProporre /></ProtectedLayout>} />
    <Route path="/admin/mutui" element={<ProtectedLayout adminOnly nuovoStile><Mutui /></ProtectedLayout>} />
    {/* Social: calendario, approvazione dei post e fotografie. Solo admin, e il server lo ricontrolla da sé */}
    <Route path="/admin/social" element={<ProtectedLayout adminOnly nuovoStile><Social /></ProtectedLayout>} />
    <Route path="/admin/mutui/:id" element={<ProtectedLayout adminOnly nuovoStile><Mutui /></ProtectedLayout>} />
    <Route path="/admin/agenzie/vecchia" element={<Navigate to="/admin/agenzie" replace />} />
    <Route path="/admin/agenzie/doppioni" element={<ProtectedLayout adminOnly nuovoStile><DoppioniAgenzie /></ProtectedLayout>} />
    <Route path="/admin/agenzie/:id" element={<ProtectedLayout adminOnly nuovoStile><SchedaAgenzia /></ProtectedLayout>} />
    <Route path="/admin/agenzie/:id/vecchia" element={<Vai a="/admin/agenzie/:id" />} />
    <Route path="/admin/agenzie/:id/modifica" element={<ProtectedLayout adminOnly nuovoStile><ModificaAgenzia /></ProtectedLayout>} />
    <Route path="/agenzie" element={<Navigate to="/admin/agenzie" replace />} />
    <Route path="/agenzie/doppioni" element={<Navigate to="/admin/agenzie/doppioni" replace />} />
    <Route path="/admin/documenti" element={<ProtectedLayout adminOnly allowCollaboratore nuovoStile><Documenti /></ProtectedLayout>} />
    <Route path="/admin/documenti/:clienteId" element={<Vai a="/admin/clienti/:clienteId#documenti" />} />
    <Route path="/admin/archivio" element={<ProtectedLayout adminOnly allowCollaboratore nuovoStile><ArchivioDocumenti /></ProtectedLayout>} />
    <Route path="/admin/impostazioni" element={<ProtectedLayout adminOnly nuovoStile><Impostazioni /></ProtectedLayout>} />
    {/* l'indirizzo vecchio del popup dell'estensione (fino alla 2.11.0): l'ancora si porta dietro */}
    <Route path="/impostazioni" element={<VaiConAncora a="/admin/impostazioni" />} />
    <Route path="/agenzie/:id" element={<Vai a="/admin/agenzie/:id" />} />
    <Route path="/agenzie/:id/modifica" element={<Vai a="/admin/agenzie/:id/modifica" />} />
    <Route path="/clienti/:id" element={<Vai a="/admin/clienti/:id" />} />
    <Route path="/matching" element={<Navigate to="/admin/clienti" replace />} />
    <Route path="/matching/:clienteId" element={<Vai a="/admin/clienti/:clienteId#archivio" />} />

    {/* Route protette - Tutti gli utenti autenticati */}
    <Route path="/collaboratore" element={<ProtectedLayout nuovoStile><OggiCollaboratore /></ProtectedLayout>} />
    <Route path="/collaboratore/clienti" element={<VecchiClientiCollaboratore />} />
    {/* Il cruscotto del mese: i propri numeri (il responsabile può guardare anche un membro del suo gruppo) */}
    <Route path="/collaboratore/mese" element={<ProtectedLayout adminOnly allowCollaboratore nuovoStile><CruscottoMese /></ProtectedLayout>} />
    <Route path="/dashboard" element={<ProtectedLayout nuovoStile><HomeCliente /></ProtectedLayout>} />
    <Route path="/profilo" element={<ProfiloPerRuolo />} />
    <Route path="/profilo/vecchio" element={<Navigate to="/profilo" replace />} />
    <Route path="/immobili" element={<ProtectedLayout nuovoStile><ImmobiliCliente /></ProtectedLayout>} />
    <Route path="/documenti" element={<DocumentiPerRuolo />} />
    {/* Rinnovo del permesso: la legge il cliente, e lo staff dalla scheda del cliente (?cliente=<id>) */}
    <Route path="/rinnovo-permesso" element={<ProtectedLayout nuovoStile><RinnovoPermesso /></ProtectedLayout>} />
    <Route path="/firma/:id" element={<ProtectedLayout nuovoStile><FirmaDocumento /></ProtectedLayout>} />
    <Route path="/visite" element={<Navigate to="/calendario" replace />} />
    <Route path="/immobili/:id" element={<ProtectedLayout nuovoStile><SchedaImmobilePerRuolo /></ProtectedLayout>} />

    {/* Indirizzo che non esiste */}
    <Route path="*" element={<NonTrovata />} />
  </Routes>
);

// Il nome del contenitore degli avvisi lo leggono i lettori di schermo: nella lingua di chi è entrato (lo staff, e
// chi non è ancora entrato, restano in italiano). Sta dentro AuthProvider perché la lingua arriva da lì.
const AvvisiConLingua = () => {
  const { userType, user } = useAuth();
  return <Toaster richColors position="top-right" containerAriaLabel={traduci(linguaDelUtente(userType, user), 'comune.avvisi')} />;
};

const App = () => {
  return (
    <AuthProvider>
      <AvvisiConLingua />
      <NotificationBanner />
      <Router>
        <div className="min-h-screen bg-background overflow-x-hidden">
          <RotteSicure />
        </div>
      </Router>
    </AuthProvider>
  );
};

export default App;
